import type { ReactNode } from "react"

import type { SessionType } from "@/types"

import { useContextMenuActions } from "@/hooks/use-context-menu-actions"
import { useTextSelection } from "@/hooks/use-text-selection"
import { useAISettingsStore } from "@/stores/ai-settings-store"

import { TextSelectionMenu } from "./text-selection-menu"

type TextSelectionProviderProps = {
  children: ReactNode
  currentSessionId: string | null
  onSendQuestion?: (params: {
    question: string
    startResearch?: boolean
    forceWebSearch?: boolean
  }) => Promise<void>
  onSetQuestion?: (question: string) => void
  onSetAskAboutText?: (text: string) => void
  sessionType?: SessionType
  projectId?: string
}

/**
 * Provider component that wraps content to enable text selection menu
 */
export function TextSelectionProvider({
  children,
  currentSessionId,
  onSendQuestion,
  onSetQuestion,
  onSetAskAboutText,
  sessionType,
  projectId,
}: TextSelectionProviderProps) {
  const enableContextualActions = useAISettingsStore(
    (state) => state.settings.enableContextualActions
  )

  const { text, position, isActive, showMenu, isPersonalContent, clearSelection, openMenu } =
    useTextSelection(enableContextualActions)

  const {
    handleAskAbout,
    handleWebSearch,
    handleWebSearchTrusted,
    handleCreateDocument,
    handleStartResearch,
    handleFactCheck,
    handleBreakOutChat,
    handleAddToMemory,
  } = useContextMenuActions({
    currentSessionId,
    onSendQuestion,
    onSetQuestion,
    onSetAskAboutText,
    sessionType,
    projectId,
  })

  return (
    <>
      {children}
      {/* Show floating button when text is selected but menu is not open */}
      {isActive && position && !showMenu && (
        <button
          aria-label="Open text selection actions menu"
          className="bg-primary text-primary-foreground border-foreground animate-in fade-in-0 zoom-in-95 focus-visible:ring-ring fixed z-[9999] flex items-center justify-center rounded-full border p-2 shadow-lg transition-[scale,box-shadow] hover:scale-110 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          data-text-selection-trigger="true"
          type="button"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
          onClick={openMenu}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              clearSelection()
            }
          }}
        >
          <svg
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {/* Show full menu when button is clicked */}
      {isActive && position && showMenu && (
        <TextSelectionMenu
          isOpen={showMenu}
          position={position}
          selectedText={text}
          sessionType={sessionType || "open"}
          suppressExportActions={isPersonalContent}
          onAskAbout={() => handleAskAbout(text)}
          onBreakOutChat={() => handleBreakOutChat(text, "user")}
          onClose={clearSelection}
          onFactCheck={() => handleFactCheck(text)}
          onStartResearch={() => handleStartResearch(text)}
          onWebSearch={() => handleWebSearch(text)}
          onWebSearchTrusted={() => handleWebSearchTrusted(text)}
          onAddToMemory={() => {
            if (isPersonalContent) return
            void handleAddToMemory(text)
          }}
          onCreateDocument={() => {
            if (isPersonalContent) return
            void handleCreateDocument(text)
          }}
        />
      )}
    </>
  )
}
